import { openWithEntity, closeWidget } from 'ducks/guruWidget/actions'
import { executeEntityActionRoutine } from 'ducks/guruRecommendations/actions'
import { GuruCtaKind } from './guruCtaKind'

const bpFolder = (bpId, folder) => `/bp-list/${bpId}/${folder}`

const navigateBp = (ctx, path, { navigate, dispatch }) => {
  if (!ctx?.contextId || ctx.contextType !== 'bp') return false
  dispatch(closeWidget())
  navigate(path)
  return true
}

const askInWidget = (question, ctx, { dispatch }) => {
  if (!ctx?.contextId || !question) return
  dispatch(openWithEntity({
    anchorContext: ctx,
    initialView: 'chat',
    prefilledInput: question,
  }))
}

// In user (global) mode the widget has no BP/portfolio to chat against —
// chat tab is hidden too. So chat-handoff CTAs route to the nearest useful
// global hub page instead of trying to open the chat.
const navigateGlobal = (path, { navigate, dispatch }) => {
  dispatch(closeWidget())
  navigate(path)
}

const HANDOFF_TO_CHAT = ({ card, ctx, ...deps }) => {
  if (ctx?.contextType === 'user') {
    // Pick a useful destination based on the sub-surface.
    const sub = ctx.subSurface
    if (sub === 'actions') return navigateGlobal('/actions', deps)
    if (sub === 'incidents') return navigateGlobal('/incidents', deps)
    if (sub === 'calendar') return navigateGlobal('/calendar', deps)
    return navigateGlobal('/tasks', deps)
  }
  askInWidget(card.title || card.ctaLabel || 'Tell me more.', ctx, deps)
}

// The "open critical actions" recommendation filters on overdue-and-open;
// keep the URL in sync with that framing so the page lands pre-filtered
// rather than dumping the user into the full open-actions list.
const REVIEW_ACTIONS = ({ ctx, navigate, dispatch }) => {
  if (ctx?.contextType === 'bp' && ctx.contextId) {
    dispatch(closeWidget())
    navigate(`/actions?bp=${ctx.contextId}&status=open&overdue=1`)
    return
  }
  if (ctx?.contextType === 'portfolio' && ctx.contextId) {
    dispatch(closeWidget())
    navigate(`/actions?portfolio=${ctx.contextId}&status=open&overdue=1`)
    return
  }
  if (ctx?.contextType === 'user') {
    dispatch(closeWidget())
    navigate('/actions?status=open&overdue=1')
    return
  }
  HANDOFF_TO_CHAT({ card: { title: 'Show me the open critical actions.' }, ctx, navigate, dispatch })
}

// "Review tasks" recommendation surfaces overdue tasks assigned to the user.
// Use the my_overdue category so it filters to tasks YOU own (not the whole
// org's overdue list, which the bare `overdue` category produces).
const WHAT_NEEDS_ATTENTION = ({ card, ctx, navigate, dispatch }) => {
  if (ctx?.contextType === 'user') {
    dispatch(closeWidget())
    navigate('/tasks?tab=tasks&category=my_overdue')
    return
  }
  if (ctx?.contextType === 'bp' && ctx.contextId) {
    dispatch(closeWidget())
    navigate(`/bp-list/${ctx.contextId}/wiki`)
    return
  }
  HANDOFF_TO_CHAT({ card: card || { title: "What's the most important thing to look at?" }, ctx, navigate, dispatch })
}

const VIEW_FILES = ({ ctx, navigate, dispatch }) => {
  if (navigateBp(ctx, bpFolder(ctx?.contextId, 'other'), { navigate, dispatch })) return
  // User mode has no global files surface — backend suppresses the card here,
  // so this branch only runs for portfolio/unknown contexts.
  HANDOFF_TO_CHAT({ card: { title: 'List the documents awaiting classification.' }, ctx, navigate, dispatch })
}

const OPEN_CHECKLIST = ({ ctx, navigate, dispatch }) => {
  if (navigateBp(ctx, `/bp-list/${ctx?.contextId}/compliance`, { navigate, dispatch })) return
  if (ctx?.contextType === 'user') {
    dispatch(closeWidget())
    navigate('/tasks?tab=compliance')
    return
  }
  HANDOFF_TO_CHAT({ card: { title: 'Open the compliance checklist for this Building Passport.' }, ctx, navigate, dispatch })
}

const LOG_INCIDENT = ({ ctx, navigate, dispatch }) => {
  if (ctx?.contextType === 'bp' && ctx.contextId) {
    dispatch(closeWidget())
    navigate(`/incidents?bp=${ctx.contextId}`)
    return
  }
  if (ctx?.contextType === 'user') {
    dispatch(closeWidget())
    navigate('/incidents')
    return
  }
  HANDOFF_TO_CHAT({ card: { title: 'Log an incident.' }, ctx, navigate, dispatch })
}

// "Draft renewal tasks" launches the Expiry Wizard — the actual drafting flow.
// In user mode we land in the global wizard; in BP mode we pre-scope it.
const DRAFT_RENEWAL_TASKS = ({ card, ctx, ...deps }) => {
  if (ctx?.contextType === 'user') {
    return navigateGlobal('/tasks?tab=tasks&view=expiry-wizard', deps)
  }
  if (ctx?.contextType === 'bp' && ctx.contextId) {
    deps.dispatch(closeWidget())
    deps.navigate(`/tasks?tab=tasks&view=expiry-wizard&bp=${ctx.contextId}`)
    return
  }
  askInWidget(
    card.title?.includes('renewal') ? card.title : 'Draft renewal tasks for expiring certificates.',
    ctx,
    deps,
  )
}

const REVIEW_MY_TASKS = ({ ctx, navigate, dispatch }) => {
  dispatch(closeWidget())
  navigate('/tasks?tab=tasks&category=my_tasks')
}

const VIEW_UPCOMING_EXPIRIES = ({ ctx, navigate, dispatch }) => {
  dispatch(closeWidget())
  navigate('/calendar')
}

// Incident CTAs: in BP context we still open chat with a contextual prefill
// (Guru can actually suggest follow-ups there). In user mode we just navigate
// to the incidents page so the label "Review incidents" is honest.
const REVIEW_INCIDENTS = ({ card, ctx, navigate, dispatch }) => {
  if (ctx?.contextType === 'user') {
    dispatch(closeWidget())
    navigate('/incidents')
    return
  }
  if (ctx?.contextType === 'bp' && ctx.contextId) {
    dispatch(closeWidget())
    navigate(`/incidents?bp=${ctx.contextId}`)
    return
  }
  HANDOFF_TO_CHAT({ card: card || { title: 'Suggest follow-up remedials for these incidents.' }, ctx, navigate, dispatch })
}

const DISPATCH_INLINE = ({ card, ctx, dispatch }) => {
  if (!ctx?.contextId) return
  dispatch(executeEntityActionRoutine({
    entityType: ctx.contextType,
    entityId: ctx.contextId,
    ctaKind: card.ctaKind,
    payload: card.ctaPayload || {},
  }))
}

export const ctaHandlers = {
  [GuruCtaKind.DRAFT_RENEWAL_TASKS]:         DRAFT_RENEWAL_TASKS,
  [GuruCtaKind.REVIEW_ACTIONS]:              REVIEW_ACTIONS,
  [GuruCtaKind.LOG_INCIDENT]:                LOG_INCIDENT,
  [GuruCtaKind.OPEN_CHECKLIST]:              OPEN_CHECKLIST,
  [GuruCtaKind.OPEN_CHAT_WITH_QUESTION]:     HANDOFF_TO_CHAT,
  [GuruCtaKind.VIEW_FILES]:                  VIEW_FILES,
  [GuruCtaKind.SUMMARISE_RECENT_ACTIVITY]:   HANDOFF_TO_CHAT,
  [GuruCtaKind.SUMMARISE_DOCUMENT]:          HANDOFF_TO_CHAT,
  [GuruCtaKind.FIND_RELATED_CERTIFICATES]:   HANDOFF_TO_CHAT,
  [GuruCtaKind.CONVERT_ACTION_TO_TASK]:      HANDOFF_TO_CHAT,
  [GuruCtaKind.SUGGEST_ASSIGNEE]:            HANDOFF_TO_CHAT,
  [GuruCtaKind.DISMISS_ACTION_WITH_REASON]:  HANDOFF_TO_CHAT,
  [GuruCtaKind.RESCHEDULE_TASK]:             HANDOFF_TO_CHAT,
  [GuruCtaKind.SUMMARISE_TASK_DEPENDENCIES]: HANDOFF_TO_CHAT,
  [GuruCtaKind.SUGGEST_INCIDENT_FOLLOWUPS]:  REVIEW_INCIDENTS,
  [GuruCtaKind.DRAFT_INCIDENT_EMAIL]:        HANDOFF_TO_CHAT,
  [GuruCtaKind.COMPLIANCE_SNAPSHOT]:         HANDOFF_TO_CHAT,
  [GuruCtaKind.WHAT_NEEDS_ATTENTION]:        WHAT_NEEDS_ATTENTION,
  [GuruCtaKind.UNIT_OCCUPANTS]:              HANDOFF_TO_CHAT,
  [GuruCtaKind.ENTITY_RECENT_ACTIVITY]:      HANDOFF_TO_CHAT,
  [GuruCtaKind.MEMBER_OPEN_TASKS]:           HANDOFF_TO_CHAT,
  [GuruCtaKind.REVIEW_MY_TASKS]:             REVIEW_MY_TASKS,
  [GuruCtaKind.VIEW_UPCOMING_EXPIRIES]:      VIEW_UPCOMING_EXPIRIES,
}

export function handleCta(card, ctx, deps) {
  const handler = ctaHandlers[card.ctaKind] || HANDOFF_TO_CHAT
  handler({ card, ctx, ...deps })
}
